// The settings page's building blocks, all on the kit's grouped look (Group / ListRow classes):
// a group whose title carries the save state, a switch row, a radio row, and `useSaver` — the one
// way every control reports "Guardando… / Guardado / No se pudo guardar · Reintentar". The state
// sits in a reserved slot beside the group title, so a save never moves the layout.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import type { AccountPreferences, AccountSnapshot } from '../../../data/features/settings';

export type SaveState = { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'error'; retry(): void; message?: string };

/**
 * `run(write)` — `write` resolves true on success. The control itself is already showing the new
 * value (optimistic); `onFail` puts the old one back. Saved fades after 2 s; an error stays, with
 * the retry repeating exactly the failed write.
 */
export function useSaver(): { state: SaveState; run(write: () => Promise<boolean>, onFail?: () => void, failMessage?: string): void } {
  const [state, setState] = useState<SaveState>({ kind: 'idle' });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generation = useRef(0);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);
  const run = useCallback((write: () => Promise<boolean>, onFail?: () => void, failMessage?: string) => {
    const mine = ++generation.current;
    if (timer.current) clearTimeout(timer.current);
    setState({ kind: 'saving' });
    const attempt = () => run(write, onFail, failMessage);
    write().then(
      (ok) => {
        if (mine !== generation.current) return;
        if (ok) {
          setState({ kind: 'saved' });
          timer.current = setTimeout(() => setState({ kind: 'idle' }), 2000);
        } else {
          onFail?.();
          setState({ kind: 'error', retry: attempt, message: failMessage });
        }
      },
      () => {
        if (mine !== generation.current) return;
        onFail?.();
        setState({ kind: 'error', retry: attempt, message: failMessage });
      },
    );
  }, []);
  return { state, run };
}

export function SaveStatus({ state }: { state: SaveState }) {
  return (
    <span className={`st-save st-save-${state.kind}`} role={state.kind === 'error' ? 'alert' : 'status'} aria-live="polite">
      {state.kind === 'saving' ? (
        <>
          <span className="st-spinner" aria-hidden />
          {t('web.settings.saving')}
        </>
      ) : state.kind === 'saved' ? (
        <>
          <Icon name="check" size={13} strokeWidth={2.4} />
          {t('web.settings.saved')}
        </>
      ) : state.kind === 'error' ? (
        <>
          {state.message ?? t('web.settings.saveFailed')}
          <button type="button" className="link-button" onClick={state.retry}>
            {t('common.retry')}
          </button>
        </>
      ) : null}
    </span>
  );
}

/** The kit's Group, with a save-state slot beside the title and an optional footer sentence. */
export function SettingsGroup({ title, save, footer, children }: { title?: string; save?: SaveState; footer?: ReactNode; children: ReactNode }) {
  return (
    <div className="group st-group">
      {title || save ? (
        <div className="group-title st-group-title">
          <span>{title}</span>
          {save ? <SaveStatus state={save} /> : null}
        </div>
      ) : null}
      <div className="group-rows">{children}</div>
      {footer ? <div className="st-footer">{footer}</div> : null}
    </div>
  );
}

/** A row whose trailing control is a switch; the whole row toggles it. */
export function SwitchRow({ icon, title, subtitle, on, disabled, onChange }: { icon?: IconName; title: ReactNode; subtitle?: ReactNode; on: boolean; disabled?: boolean; onChange(on: boolean): void }) {
  return (
    <button type="button" className="list-row clickable st-switch-row" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)}>
      {icon ? (
        <span className="list-row-leading">
          <Icon name={icon} size={18} />
        </span>
      ) : null}
      <span className="list-row-text">
        <span className="list-row-title st-wrap">{title}</span>
        {subtitle ? <span className="list-row-subtitle">{subtitle}</span> : null}
      </span>
      <span className={`st-switch${on ? ' on' : ''}`} aria-hidden />
    </button>
  );
}

/** One choice of a single-choice list: a check on the chosen one (the Mac's picker rows). */
export function RadioRow({ title, subtitle, leading, checked, disabled, onPick }: { title: ReactNode; subtitle?: ReactNode; leading?: ReactNode; checked: boolean; disabled?: boolean; onPick(e: React.MouseEvent<HTMLButtonElement>): void }) {
  return (
    <button type="button" className={`list-row clickable st-radio-row${checked ? ' checked' : ''}`} role="radio" aria-checked={checked} disabled={disabled} onClick={(e) => onPick(e)}>
      {leading ? <span className="list-row-leading">{leading}</span> : null}
      <span className="list-row-text">
        <span className="list-row-title st-wrap">{title}</span>
        {subtitle ? <span className="list-row-subtitle">{subtitle}</span> : null}
      </span>
      <span className="st-check" aria-hidden>
        {checked ? <Icon name="check" size={16} strokeWidth={2.4} /> : null}
      </span>
    </button>
  );
}

export function PresenceDot({ status, size = 10 }: { status: string; size?: number }) {
  return <span className={`presence st-dot ${status}`} style={{ width: size, height: size }} aria-hidden />;
}

/** The account as the SERVER has it, shared by every section of one settings page. */
export interface AccountState {
  account: AccountSnapshot | null;
  loadError: string | null;
  reload(): void;
  /** A section that wrote something folds the server's answer back in (no refetch). */
  patch(change: Partial<AccountSnapshot>): void;
  prefs: AccountPreferences | null;
  setPrefs(p: AccountPreferences): void;
}

export const AccountContext = createContext<AccountState | null>(null);

export function useAccount(): AccountState {
  const a = useContext(AccountContext);
  if (!a) throw new Error('useAccount outside the settings page');
  return a;
}
